import { Lock, Plus, Trash2 } from 'lucide-react';
import { Button, Input, Label, Textarea } from '@buzzbee/ui';

export type Hours = Partial<Record<'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun', { opens: string; closes: string }[]>>;
export type Service = { id: string; name: string; duration_minutes: number; requires_supervision?: boolean; price_cents?: number; deposit_cents?: number; rebook_after_days?: number };

/**
 * Optional per-service growth settings in the editor: true = editable, false = shown locked (the
 * plan lacks the feature; the Front desk page has the upgrade prompt). Omitted = not shown.
 */
export type ServiceExtras = { rebooking?: boolean; deposits?: boolean };

const LOCKED = 'Part of ReceptionistBee: see Front desk to upgrade';

/** Dollars typed by the owner to cents (empty = none). */
const toCents = (v: string) => (v.trim() === '' ? undefined : Math.max(0, Math.round(Number(v) * 100)) || undefined);
export type Faq = { question: string; answer: string };

const DAYS: [keyof Hours, string][] = [
  ['mon', 'Monday'],
  ['tue', 'Tuesday'],
  ['wed', 'Wednesday'],
  ['thu', 'Thursday'],
  ['fri', 'Friday'],
  ['sat', 'Saturday'],
  ['sun', 'Sunday'],
];

export function HoursEditor({ value, onChange }: { value: Hours; onChange: (h: Hours) => void }) {
  return (
    <div className="space-y-2">
      {DAYS.map(([key, label]) => {
        const w = value[key]?.[0];
        return (
          <div key={key} className="flex flex-wrap items-center gap-3">
            <label className="flex w-36 items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={!!w}
                onChange={(e) => {
                  const next = { ...value };
                  if (e.target.checked) next[key] = [{ opens: '09:00', closes: '17:00' }];
                  else delete next[key];
                  onChange(next);
                }}
              />
              {label}
            </label>
            {w ? (
              <>
                <Input aria-label={`${label} opens`} type="time" className="w-32" value={w.opens} onChange={(e) => onChange({ ...value, [key]: [{ ...w, opens: e.target.value }] })} />
                <span className="text-sm text-muted-foreground">to</span>
                <Input aria-label={`${label} closes`} type="time" className="w-32" value={w.closes} onChange={(e) => onChange({ ...value, [key]: [{ ...w, closes: e.target.value }] })} />
              </>
            ) : (
              <span className="text-sm text-muted-foreground">Closed</span>
            )}
          </div>
        );
      })}
    </div>
  );
}

const slug = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 50) || `service-${Date.now()}`;

export function ServicesEditor({ value, onChange, extras }: { value: Service[]; onChange: (s: Service[]) => void; extras?: ServiceExtras }) {
  return (
    <div className="space-y-2">
      {value.map((s, i) => (
        <div key={`${s.id}-${i}`} className="flex flex-wrap items-center gap-2">
          <Input aria-label="Service name" className="min-w-48 flex-1" value={s.name} onChange={(e) => onChange(value.map((x, j) => (j === i ? { ...x, name: e.target.value, id: x.id || slug(e.target.value) } : x)))} />
          <Input aria-label="Minutes" type="number" min={5} max={720} className="w-24" value={s.duration_minutes} onChange={(e) => onChange(value.map((x, j) => (j === i ? { ...x, duration_minutes: Number(e.target.value) } : x)))} />
          <span className="text-sm text-muted-foreground">min</span>
          <label className="flex items-center gap-1 text-xs text-muted-foreground">
            <input type="checkbox" checked={!!s.requires_supervision} onChange={(e) => onChange(value.map((x, j) => (j === i ? { ...x, requires_supervision: e.target.checked } : x)))} /> Needs staff OK
          </label>
          {extras?.deposits !== undefined ? (
            <label className="flex items-center gap-1 text-xs text-muted-foreground" title={extras.deposits ? 'Deposit to hold the time (empty = none)' : LOCKED}>
              {extras.deposits ? null : <Lock className="size-3" aria-label="Locked" />}
              Deposit $
              <Input aria-label="Deposit (dollars)" type="number" min={0} step="0.01" className="w-20" disabled={!extras.deposits} value={s.deposit_cents ? String(s.deposit_cents / 100) : ''} onChange={(e) => onChange(value.map((x, j) => (j === i ? { ...x, deposit_cents: toCents(e.target.value) } : x)))} />
            </label>
          ) : null}
          {extras?.rebooking !== undefined ? (
            <label className="flex items-center gap-1 text-xs text-muted-foreground" title={extras.rebooking ? 'Suggest the next visit this many days after one (empty = your default)' : LOCKED}>
              {extras.rebooking ? null : <Lock className="size-3" aria-label="Locked" />}
              Rebook after
              <Input aria-label="Suggest rebooking after (days)" type="number" min={1} max={365} className="w-16" disabled={!extras.rebooking} value={s.rebook_after_days ?? ''} onChange={(e) => onChange(value.map((x, j) => (j === i ? { ...x, rebook_after_days: e.target.value ? Math.min(365, Math.max(1, Math.round(Number(e.target.value)))) : undefined } : x)))} />
              days
            </label>
          ) : null}
          <Button variant="ghost" size="icon" aria-label="Remove service" onClick={() => onChange(value.filter((_, j) => j !== i))}>
            <Trash2 />
          </Button>
        </div>
      ))}
      <Button variant="outline" size="sm" onClick={() => onChange([...value, { id: '', name: '', duration_minutes: 30 }])}>
        <Plus /> Add service
      </Button>
    </div>
  );
}

export function normalizeServices(list: Service[]): Service[] {
  const seen = new Set<string>();
  return list
    .filter((s) => s.name.trim())
    .map((s) => {
      let id = s.id || slug(s.name);
      while (seen.has(id)) id = `${id}-2`;
      seen.add(id);
      return { ...s, id, name: s.name.trim() };
    });
}

export function FaqEditor({ value, onChange }: { value: Faq[]; onChange: (f: Faq[]) => void }) {
  return (
    <div className="space-y-3">
      {value.map((f, i) => (
        <div key={i} className="grid gap-2 rounded-lg bg-muted/40 p-3">
          <div className="flex items-center gap-2">
            <Label className="sr-only" htmlFor={`faq-q-${i}`}>
              Question
            </Label>
            <Input id={`faq-q-${i}`} placeholder="Question" value={f.question} onChange={(e) => onChange(value.map((x, j) => (j === i ? { ...x, question: e.target.value } : x)))} />
            <Button variant="ghost" size="icon" aria-label="Remove question" onClick={() => onChange(value.filter((_, j) => j !== i))}>
              <Trash2 />
            </Button>
          </div>
          <Textarea aria-label="Answer" placeholder="Answer" rows={2} value={f.answer} onChange={(e) => onChange(value.map((x, j) => (j === i ? { ...x, answer: e.target.value } : x)))} />
        </div>
      ))}
      <Button variant="outline" size="sm" onClick={() => onChange([...value, { question: '', answer: '' }])}>
        <Plus /> Add question
      </Button>
    </div>
  );
}

export const LANGUAGES = [
  { code: 'en', name: 'English' },
  { code: 'fr', name: 'French' },
  { code: 'es', name: 'Spanish' },
  { code: 'hi', name: 'Hindi' },
  { code: 'pa', name: 'Punjabi' },
] as const;
